import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../../services/authService';

const AgentPasswordResetFlow = () => {
  const [step, setStep] = useState('enterEmail'); // 'enterEmail', 'chooseMethod', 'enterPassword', 'enter2FACode'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('');
  const [availableMethods, setAvailableMethods] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [token, setToken] = useState(null);
  const [agentInfo, setAgentInfo] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const resetToken = queryParams.get('token');
    if (resetToken) {
      setToken(resetToken);
      setStep('enterPassword');
    }
  }, [location]);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      const response = await authService.requestAgentPasswordReset({ email });
      
      // Get the reset token from the response
      setMessage(response.message);
      
      // Backend already sent the password reset email automatically
      // Skip method selection and go directly to email sent confirmation
      setSelectedMethod('email');
      setStep('emailSent');
      
    } catch (err) {
      setError('Failed to process request. Please try again.');
    }
  };

  const handleMethodSelection = async (method) => {
    console.log('Method selection clicked:', method);
    console.log('Current token:', token);
    
    setSelectedMethod(method);
    setError('');
    setMessage('');

    if (!token) {
      // Send 2FA code immediately after method selection
      try {
        console.log('Sending 2FA code for method:', method, 'to email:', email);
        const response = await authService.sendPasswordReset2FA({ email, method });
        console.log('2FA code sent response:', response);
        
        if (response.success) {
          setToken(response.token);
          const methodMessage = method === 'sms' 
            ? `A verification code has been sent to your phone ending in ${response.phoneLastFour || 'XXXX'}.`
            : 'A verification code has been sent to your email.';
          setMessage(methodMessage);
          setStep('enter2FACode');
        } else {
          setError('Failed to send verification code. Please try again.');
        }
      } catch (err) {
        console.error('2FA code sending error:', err);
        setError(`Failed to send verification code: ${err.response?.data?.error || err.message}`);
      }
      return;
    }

    try {
      console.log('Calling choosePasswordReset2FAMethod with:', { token, method });
      const response = await authService.choosePasswordReset2FAMethod({ token, method });
      console.log('Method selection response:', response);
      
      setMessage(`2FA method updated to ${method}. You can now proceed with password reset.`);
      setStep('enterPassword');
    } catch (err) {
      console.error('Method selection error:', err);
      setError(`Failed to update 2FA method: ${err.response?.data?.error || err.message}`);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }
    setError('');
    setMessage('');

    try {
      console.log('Finalizing password reset with:', { token, password: '***' });
      
      // Complete the password reset using the existing token
      const response = await authService.confirmAgentPasswordReset({ 
        token: token,
        newPassword: password
      });
      
      console.log('Password reset completion response:', response);
      
      if (response.success) {
        setMessage('Password reset successful! Redirecting to dashboard...');
        setTimeout(() => navigate('/agent/dashboard'), 2000);
      } else {
        setError('Failed to complete password reset. Please try again.');
      }
    } catch (err) {
      console.error('Password reset completion error:', err);
      setError(`Failed to reset password: ${err.response?.data?.error || err.message}`);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    try {
      const response = await authService.verifyPasswordReset2FA({ token, twoFactorCode });
      console.log('2FA verification response:', response);
      
      if (response.success) {
        setMessage('2FA verification successful! Please set your new password.');
        setStep('enterPassword');
      } else {
        setError('Invalid or expired 2FA code. Please try again.');
      }
    } catch (err) {
      console.error('2FA verification error:', err);
      setError(`Invalid or expired 2FA code: ${err.response?.data?.error || err.message}`);
    }
  };

  const getMethodIcon = (method) => {
    switch (method) {
      case 'email':
        return '📧';
      case 'sms':
        return '📱';
      default:
        return '🔐';
    }
  };

  const getMethodDescription = (method) => {
    switch (method) {
      case 'email':
        return 'Receive verification code via email';
      case 'sms':
        return 'Receive verification code via text message';
      default:
        return '';
    }
  };

  const get2FAInstructions = (method) => {
    switch (method) {
      case 'email':
        return 'A verification code has been sent to your email address.';
      case 'sms':
        return 'A verification code has been sent to your phone.';
      default:
        return 'A verification code has been sent.';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center">
      <div className="max-w-md w-full bg-white p-8 rounded-lg shadow-md">
        
        {/* Step 1: Enter Email */}
        {step === 'enterEmail' && (
          <div>
            <h2 className="text-2xl font-bold text-center mb-6">Reset Your Password</h2>
            <p className="text-center text-gray-600 mb-6">Enter your email address to begin the password reset process.</p>
            <form onSubmit={handleEmailSubmit}>
              <div className="mb-4">
                <label htmlFor="email" className="block text-gray-700 font-bold mb-2">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring focus:ring-blue-200"
                  required
                />
              </div>
              <button type="submit" className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition duration-200">
                Continue
              </button>
            </form>
          </div>
        )}

        {/* Step 2: Choose 2FA Method - REMOVED */}
        {/* This step was confusing because the backend already sends the password reset email */}
        {/* Users now go directly from email submission to "Check Your Email" confirmation */}

        {/* Step 2: Email Sent Confirmation */}
        {step === 'emailSent' && (
          <div className="text-center">
            <div className="text-6xl mb-4">📧</div>
            <h2 className="text-2xl font-bold mb-4">Check Your Email</h2>
            <p className="text-gray-600 mb-6">
              If an account with that email exists, a password reset link has been sent to your email address. Please check your inbox and click the link to continue.
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Didn't receive the email? Check your spam folder or wait a few minutes.
            </p>
            <button 
              onClick={() => {setStep('enterEmail'); setEmail(''); setMessage(''); setError('');}}
              className="text-blue-500 hover:text-blue-700 underline"
            >
              Try a different email address
            </button>
          </div>
        )}

        {/* Step 3: Enter New Password */}
        {step === 'enterPassword' && (
          <div>
            <h2 className="text-2xl font-bold text-center mb-6">Set New Password</h2>
            <form onSubmit={handlePasswordSubmit}>
              <div className="mb-4">
                <label htmlFor="password" className="block text-gray-700 font-bold mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring focus:ring-blue-200"
                    minLength="8"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="text-sm text-gray-600 mt-1">Password must be at least 8 characters long</div>
              </div>
              <div className="mb-6">
                <label htmlFor="confirmPassword" className="block text-gray-700 font-bold mb-2">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full px-3 py-2 pr-10 border rounded-lg focus:outline-none focus:ring ${
                      confirmPassword && password !== confirmPassword 
                        ? 'border-red-500 focus:ring-red-200' 
                        : 'focus:ring-blue-200'
                    }`}
                    minLength="8"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <div className="text-sm text-red-600 mt-1">Passwords do not match</div>
                )}
                {confirmPassword && password === confirmPassword && confirmPassword.length >= 8 && (
                  <div className="text-sm text-green-600 mt-1">✓ Passwords match</div>
                )}
              </div>
              <button type="submit" className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition duration-200">
                Set Password
              </button>
            </form>
          </div>
        )}

        {/* Step 4: Enter 2FA Code */}
        {step === 'enter2FACode' && (
          <div>
            <h2 className="text-2xl font-bold text-center mb-6">Enter Verification Code</h2>
            <div className="text-center mb-6">
              <span className="text-4xl">{getMethodIcon(selectedMethod)}</span>
              <p className="text-gray-600 mt-2">{get2FAInstructions(selectedMethod)}</p>
            </div>
            <form onSubmit={handle2FASubmit}>
              <div className="mb-4">
                <label htmlFor="twoFactorCode" className="block text-gray-700 font-bold mb-2">6-Digit Code</label>
                <input
                  type="text"
                  id="twoFactorCode"
                  value={twoFactorCode}
                  onChange={(e) => setTwoFactorCode(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-center tracking-widest text-lg focus:outline-none focus:ring focus:ring-blue-200"
                  maxLength="6"
                  pattern="[0-9]{6}"
                  required
                />
              </div>
              <button type="submit" className="w-full bg-blue-500 text-white py-2 rounded-lg hover:bg-blue-600 transition duration-200">
                Verify & Complete Reset
              </button>
            </form>
          </div>
        )}

        {/* Messages */}
        {message && (
          <div className="mt-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {message}
          </div>
        )}
        {error && (
          <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* Progress Indicator */}
        <div className="mt-6 flex justify-center space-x-2">
          {['enterEmail', 'emailSent', 'enterPassword'].map((stepName, index) => (
            <div
              key={stepName}
              className={`w-3 h-3 rounded-full ${
                step === stepName ? 'bg-blue-500' : 
                ['enterEmail', 'emailSent', 'enterPassword'].indexOf(step) > index ? 'bg-green-500' : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default AgentPasswordResetFlow;
